import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  Icon,
  Input,
  Spinner,
} from "../../design-system";
import {
  getWorkSchedules,
  createWorkSchedule,
  updateWorkSchedule,
} from "../../helpers/kronaly_helper";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const DAY_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

function unwrap(r) {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
}

function dayMinutes(d) {
  if (!d?.active) return 0;
  const [sh, sm] = d.start.split(":").map(Number);
  const [eh, em] = d.end.split(":").map(Number);
  return Math.max(0, eh * 60 + em - (sh * 60 + sm) - (Number(d.pause) || 0));
}

function daysToConfig(days) {
  const out = {};
  days.forEach((d, i) => {
    const key = DAY_KEYS[i];
    if (!d.active) {
      out[key] = { total_minutes: 0 };
      return;
    }
    const total = dayMinutes(d);
    const entry = { start: d.start, end: d.end, total_minutes: total };
    if (Number(d.pause) > 0) {
      entry.break = { minutes: Number(d.pause) };
    }
    out[key] = entry;
  });
  return out;
}

function configToDays(config) {
  const cfgDays = config?.days || {};
  return DAY_KEYS.map((k) => {
    const d = cfgDays[k] || {};
    const total = Number(d.total_minutes || 0);
    return {
      active: total > 0,
      start: d.start || "09:00",
      end: d.end || "18:00",
      pause: Number(d?.break?.minutes ?? 0),
    };
  });
}

function summarizeSchedule(model) {
  const days = configToDays(model?.config || {});
  const activeIdx = days.map((d, i) => (d.active ? i : -1)).filter((i) => i >= 0);
  const daysLabel = activeIdx.map((i) => DAY_SHORT[i]).join(" ");
  const first = activeIdx.length > 0 ? days[activeIdx[0]] : null;
  return {
    daysLabel: daysLabel || "—",
    start: first?.start || "—",
    end: first?.end || "—",
    pause: first ? Number(first.pause || 0) : 0,
    weeklyHours: Number(model?.weekly_minutes || 0) / 60,
  };
}

function Horarios() {
  document.title = "Horarios | Kronaly";

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getWorkSchedules();
      const data = unwrap(r);
      setSchedules(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los horarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => setEditing({});
  const openEdit = (schedule) => setEditing(schedule);
  const closeEditor = () => setEditing(null);
  const isEditing = !!editing?.id;
  const onSaved = async () => {
    setEditing(null);
    await load();
  };

  return (
    <div className="kr-page">
      <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>Configuración</div>
            <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>Horarios</h1>
            <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)" }}>Plantillas de horarios laborales que se asignan a contratos.</p>
          </div>
          <Button variant="primary" icon="Plus" onClick={openNew}>Nuevo horario</Button>
        </div>

        {error && <Alert tone="danger">{error}</Alert>}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <Spinner />
          </div>
        ) : schedules.length === 0 ? (
          <Card>
            <EmptyState
              icon="Clock"
              title="Aún no hay horarios"
              description="Crea el primer horario para poder asignarlo a contratos."
              action={<Button variant="primary" icon="Plus" onClick={openNew}>Nuevo horario</Button>}
            />
          </Card>
        ) : (
          <Card padded={false}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "2fr 1.4fr 0.8fr 0.8fr 0.8fr 0.8fr",
              padding: "10px 20px", background: "var(--kr-neutral-50)",
              borderBottom: "1px solid var(--kr-border)",
              fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em",
              fontWeight: 600, color: "var(--kr-fg-muted)",
            }}>
              <span>Nombre</span><span>Días</span><span>Entrada</span><span>Salida</span><span>Pausa</span><span>Semanal</span>
            </div>
            {schedules.map((s, i) => (
              <ScheduleRow key={s.id || s.name} schedule={s} last={i === schedules.length - 1} onEdit={openEdit} />
            ))}
          </Card>
        )}
      </div>

      <Drawer
        open={!!editing}
        onClose={closeEditor}
        size="xl"
        title={
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{isEditing ? "Editar horario" : "Nuevo horario"}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{isEditing ? editing.name : "Configurar plantilla"}</div>
          </div>
        }
      >
        {editing && <HorarioEditor schedule={editing} onClose={closeEditor} onSaved={onSaved} />}
      </Drawer>
    </div>
  );
}

function ScheduleRow({ schedule, last, onEdit }) {
  const sum = summarizeSchedule(schedule);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit?.(schedule)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit?.(schedule);
        }
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--kr-neutral-50)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
      style={{
        display: "grid", gridTemplateColumns: "2fr 1.4fr 0.8fr 0.8fr 0.8fr 0.8fr", gap: 12,
        padding: "12px 20px", borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: "#fff", fontSize: 13, alignItems: "center", cursor: "pointer",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 6, background: "var(--kr-action-soft-bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="Clock" size={14} style={{ color: "var(--kr-action)" }} />
        </div>
        <span style={{ fontWeight: 500, color: "var(--kr-fg-strong)" }}>{schedule.name}</span>
      </div>
      <div style={{ display: "flex", gap: 3 }}>
        {DAY_SHORT.map((d) => (
          <span key={d} style={{
            width: 22, height: 22, borderRadius: 4, fontSize: 10, fontWeight: 600,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: sum.daysLabel.includes(d) ? "var(--kr-action-soft-bg)" : "var(--kr-neutral-100)",
            color: sum.daysLabel.includes(d) ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
            fontFamily: "var(--kr-font-mono)",
          }}>{d}</span>
        ))}
      </div>
      <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)" }}>{sum.start}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)" }}>{sum.end}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-muted)" }}>{sum.pause}m</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", fontWeight: 600 }}>{sum.weeklyHours.toFixed(1)} h</div>
    </div>
  );
}

const DEFAULT_DAYS = [
  { active: true, start: "09:00", end: "18:00", pause: 60 },
  { active: true, start: "09:00", end: "18:00", pause: 60 },
  { active: true, start: "09:00", end: "18:00", pause: 60 },
  { active: true, start: "09:00", end: "18:00", pause: 60 },
  { active: true, start: "09:00", end: "18:00", pause: 60 },
  { active: false, start: "09:00", end: "14:00", pause: 0 },
  { active: false, start: "09:00", end: "14:00", pause: 0 },
];

function HorarioEditor({ schedule, onClose, onSaved }) {
  const editingId = schedule?.id || null;
  const [name, setName] = useState(schedule?.name || "");
  const [days, setDays] = useState(
    editingId ? configToDays(schedule?.config || {}) : DEFAULT_DAYS
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const totalWeekly = days.reduce((acc, d) => acc + dayMinutes(d) / 60, 0);
  const validHours = totalWeekly >= 20 && totalWeekly <= 50;
  const hasActive = days.some((d) => d.active);
  const canSubmit = name.trim().length > 0 && hasActive && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const config = {
        timezone:
          schedule?.config?.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          "Europe/Madrid",
        days: daysToConfig(days),
      };
      const weekly_minutes = days.reduce((acc, d) => acc + dayMinutes(d), 0);
      const payload = { name: name.trim(), weekly_minutes, config };
      if (editingId) {
        await updateWorkSchedule(editingId, payload);
      } else {
        await createWorkSchedule(payload);
      }
      onSaved();
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo guardar el horario");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", margin: -20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", flex: 1, minHeight: 0 }}>
        <div style={{ padding: 24, overflow: "auto" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "var(--kr-fg-strong)" }}>Nombre del horario *</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Flexible 9-18" />
            </label>
          </div>

          {error && <div style={{ marginBottom: 12 }}><Alert tone="danger">{error}</Alert></div>}

          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", margin: "8px 0 8px" }}>Configuración semanal</div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginBottom: 14 }}>Activa los días laborables y define el horario para cada uno.</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {DAY_LABELS.map((label, i) => {
              const d = days[i];
              const setDay = (patch) => setDays(days.map((dd, di) => (di === i ? { ...dd, ...patch } : dd)));
              const dayMins = dayMinutes(d);
              const dayHours = (dayMins / 60).toFixed(1);
              return (
                <div key={i} style={{
                  display: "grid", gridTemplateColumns: "120px 1fr 1fr 70px 80px",
                  gap: 8, alignItems: "center",
                  padding: 12, borderRadius: 8,
                  background: d.active ? "#fff" : "var(--kr-neutral-50)",
                  border: "1px solid var(--kr-border)",
                  opacity: d.active ? 1 : 0.55,
                }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", cursor: "pointer" }}>
                    <input type="checkbox" checked={d.active} onChange={(e) => setDay({ active: e.target.checked })} style={{ accentColor: "var(--kr-action)" }} />
                    {label}
                  </label>
                  <TimeInputH value={d.start} onChange={(v) => setDay({ start: v })} disabled={!d.active} />
                  <TimeInputH value={d.end} onChange={(v) => setDay({ end: v })} disabled={!d.active} />
                  <PauseInputH value={d.pause} onChange={(v) => setDay({ pause: v })} disabled={!d.active} />
                  <div style={{ fontFamily: "var(--kr-font-mono)", fontVariantNumeric: "tabular-nums", fontWeight: 600, color: "var(--kr-fg-strong)", textAlign: "right" }}>{d.active ? `${dayHours}h` : "—"}</div>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
            <Button variant="ghost" size="sm" onClick={() => setDays(days.map((d, i) => ({ ...d, active: i < 5 ? d.active : !d.active })))}>Activar fin de semana</Button>
            <Button variant="ghost" size="sm" onClick={() => setDays(days.map(() => ({ active: true, start: "09:00", end: "18:00", pause: 60 })))}>Aplicar a todos</Button>
          </div>
        </div>

        <div style={{ borderLeft: "1px solid var(--kr-border)", background: "var(--kr-neutral-50)", padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 6 }}>Resumen semanal</div>
            <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 36, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.02em" }}>
              {totalWeekly.toFixed(1)}<span style={{ fontSize: 18, color: "var(--kr-fg-muted)", marginLeft: 4 }}>h/sem</span>
            </div>
            <div style={{ marginTop: 8 }}>
              {validHours
                ? <Badge tone="success" dot>Dentro del rango legal</Badge>
                : <Badge tone="warning" dot>Fuera del rango habitual</Badge>}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 8 }}>Visualización diaria</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {DAY_LABELS.map((label, i) => {
                const d = days[i];
                if (!d.active) return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 32, fontSize: 11, color: "var(--kr-fg-muted)", fontWeight: 600 }}>{label.slice(0, 3)}</span>
                    <div style={{ flex: 1, height: 14, background: "var(--kr-neutral-100)", borderRadius: 3 }} />
                  </div>
                );
                const [sh, sm] = d.start.split(":").map(Number);
                const [eh, em] = d.end.split(":").map(Number);
                const startPct = ((sh * 60 + sm) / 1440) * 100;
                const endPct = ((eh * 60 + em) / 1440) * 100;
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 32, fontSize: 11, color: "var(--kr-fg-strong)", fontWeight: 600 }}>{label.slice(0, 3)}</span>
                    <div style={{ flex: 1, height: 14, background: "var(--kr-neutral-100)", borderRadius: 3, position: "relative" }}>
                      <div style={{ position: "absolute", left: `${startPct}%`, width: `${endPct - startPct}%`, top: 0, bottom: 0, background: "var(--kr-action)", borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 8 }}>Validaciones</div>
            <ValidationItem ok={name.trim().length > 0} label="Nombre obligatorio" />
            <ValidationItem ok={hasActive} label="Al menos un día activo" />
            <ValidationItem ok={totalWeekly >= 20} label="Mínimo 20h semanales" />
            <ValidationItem ok={totalWeekly <= 50} label="Máximo 50h semanales" />
          </div>
        </div>
      </div>

      <div style={{ padding: "12px 20px", borderTop: "1px solid var(--kr-border)", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fff" }}>
        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="primary" icon="Check" onClick={submit} disabled={!canSubmit}>
          {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Guardar horario"}
        </Button>
      </div>
    </div>
  );
}

function TimeInputH({ value, onChange, disabled }) {
  return (
    <input type="time" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      style={{
        height: 32, border: "1px solid var(--kr-border)", borderRadius: 6, padding: "0 10px",
        fontSize: 13, fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", background: "#fff", outline: "none",
      }} />
  );
}

function PauseInputH({ value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", alignItems: "center", border: "1px solid var(--kr-border)", borderRadius: 6, height: 32, padding: "0 8px", background: "#fff" }}>
      <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} disabled={disabled}
        style={{ width: "100%", border: "none", outline: "none", fontSize: 13, fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", background: "transparent" }} />
      <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>min</span>
    </div>
  );
}

function ValidationItem({ ok, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12 }}>
      <Icon name={ok ? "CheckCircle2" : "XCircle"} size={14} style={{ color: ok ? "var(--kr-success-700)" : "var(--kr-danger-700)" }} />
      <span style={{ color: ok ? "var(--kr-fg-strong)" : "var(--kr-danger-700)" }}>{label}</span>
    </div>
  );
}

export default Horarios;
